import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import type {
  StoreRequirementsData,
  StoreActivityData,
  ProductRequirementWithProof,
} from "../lib/proofing-flow";

// Re-export calculator types for consumers
export type {
  StoreRequirementsData,
  StoreActivityData,
  ProductRequirementWithProof,
};

// Types matching the JSONB structure from get_store_proof_requirements
export type ProofInfo = {
  proof_id: number;
  status: "pending" | "approved" | "rejected";
  image_url: string;
  created_at: string;
} | null;

/**
 * Store proof requirements state.
 * Now dynamic — driven by requirement templates from the database.
 * Each store activity carries its own product_requirements array.
 */
export type StoreProofRequirements = StoreRequirementsData | null;

// Atom keyed by store_id
const requirementsAtom = atom<Record<number, StoreProofRequirements>>({});

/**
 * Raw RPC response shape for product requirements.
 * The RPC returns `product_requirement_id` instead of `id` and omits `description`.
 */
interface RpcProductRequirement {
  product_requirement_id: number;
  code: string;
  labels: Record<string, string>;
  order: number;
  proof: ProductRequirementWithProof["proof"];
}

/**
 * Raw RPC response shape for store activities.
 */
interface RpcStoreActivity {
  store_activity_id: number;
  is_fulfilled: boolean;
  product_id: number | null;
  product_name: string | null;
  period_id: number;
  requirement_template_id: number;
  product_requirements: RpcProductRequirement[];
}

/**
 * Raw RPC response (before transformation).
 */
interface RpcStoreProofRequirements {
  store: {
    id: number;
    name: string;
    latitude: number | null;
    longitude: number | null;
  };
  store_proof: ProofInfo;
  store_activities: RpcStoreActivity[];
}

/**
 * Transform the RPC response into the shape expected by the proofing flow calculator.
 * Maps `product_requirement_id` → `id` and provides a default for `description`.
 * Populates `require_store` from the templates map.
 */
function transformRpcResponse(
  data: RpcStoreProofRequirements,
  templates: Map<number, boolean>,
): StoreRequirementsData {
  return {
    store: data.store,
    store_proof: data.store_proof,
    store_activities: data.store_activities.map((sa) => ({
      store_activity_id: sa.store_activity_id,
      is_fulfilled: sa.is_fulfilled,
      product_id: sa.product_id,
      product_name: sa.product_name,
      period_id: sa.period_id,
      requirement_template_id: sa.requirement_template_id,
      require_store: templates.get(sa.requirement_template_id) ?? true,
      product_requirements: (sa.product_requirements || []).map((pr) => ({
        id: pr.product_requirement_id,
        code: pr.code,
        description: pr.code, // RPC omits description; use code as fallback
        labels: pr.labels,
        order: pr.order,
        proof: pr.proof,
      })),
    })),
  };
}

export function useStoreProofRequirements(
  storeId: number | undefined,
  userIdOverride?: string,
) {
  const [requirementsMap, setRequirementsMap] = useAtom(requirementsAtom);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  const effectiveUserId = userIdOverride ?? user?.id;

  const requirements = storeId ? requirementsMap[storeId] : null;

  const refetch = useCallback(async () => {
    if (!storeId || !effectiveUserId) return;

    setLoading(true);

    // Fetch store proof requirements and templates in parallel
    const [reqResult, templatesResult] = await Promise.all([
      supabase.rpc("get_store_proof_requirements", {
        p_store_id: storeId,
        p_user_id: effectiveUserId,
      }),
      supabase.from("requirement_templates").select("id, require_store"),
    ]);

    if (reqResult.error) {
      console.error(
        "Error fetching store proof requirements:",
        reqResult.error,
      );
      setLoading(false);
      return;
    }

    // Build a template map: id → require_store
    const templates = new Map<number, boolean>();
    if (templatesResult.data) {
      for (const t of templatesResult.data) {
        templates.set(t.id, t.require_store);
      }
    }

    setRequirementsMap((prev) => ({
      ...prev,
      [storeId]: transformRpcResponse(
        reqResult.data as unknown as RpcStoreProofRequirements,
        templates,
      ),
    }));
    setLoading(false);
  }, [storeId, effectiveUserId, setRequirementsMap]);

  useEffect(() => {
    if (storeId && effectiveUserId && !requirements) {
      refetch();
    } else if (requirements) {
      setLoading(false);
    }
  }, [storeId, effectiveUserId, requirements, refetch]);

  return { requirements, loading, refetch };
}

export function useResetStoreProofRequirements() {
  const [, setRequirementsMap] = useAtom(requirementsAtom);
  return (storeId?: number) => {
    if (storeId) {
      setRequirementsMap((prev) => {
        const next = { ...prev };
        delete next[storeId];
        return next;
      });
    } else {
      setRequirementsMap({});
    }
  };
}
